import { queryClient } from '@/context/CombinedProvider';
import {
  ICheckoutItem,
  ICheckoutResponse,
  IFulfillmentDetails,
  IMutationQuery,
  IOrder,
  IQuery,
  ISKU,
  ISKURedemption,
  IUserConfig,
} from '@/types';
import {
  decodeAuthToken,
  employeeExperienceAxiosInstance,
  employeeExperienceV3AxiosInstance,
  ordersAxiosInstance,
} from '@/utils';
import { authenticateMeQuery } from './auth.queries';
import AppConfig from '@/AppConfig';

export const getSkuDetails = (skuId: string = AppConfig.CULT_SKU_ID): IQuery<ISKU> => {
  return {
    queryKey: ['sku-details'],
    queryFn: async () => {
      const url = `/catalogue/skus/${skuId}`;
      const response = await employeeExperienceAxiosInstance.get<ISKU>(url);
      return response.data;
    },
  };
};

/**
 * Generates an idempotent key for the user's cart, valid for 10 minutes.
 * Uses Web Crypto API for hashing.
 *
 * @param customerId - The ID of the user.
 * @param cartItems - The list of items in the cart.
 * @returns A Promise resolving to the idempotent key (SHA-256 hash in hex).
 */
async function generateIdempotentKey(
  userId: string,
  cartItems: { id: string; quantity: number }[],
): Promise<string> {
  // Round timestamp to the nearest 10-minute interval
  const timestamp = Math.floor(Date.now() / (10 * 60 * 1000));

  // sort so that the order is consistent
  const cartString = JSON.stringify(
    cartItems.sort((a, b) => a.id.localeCompare(b.id)),
  );

  // Concatenate user ID, cart contents, and timestamp
  const data = `${userId}:${cartString}:${timestamp}`;

  // Convert to Uint8Array for hashing
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);

  // Compute SHA-256 hash using Web Crypto API
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);

  // Convert hash to hex string
  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

export const instantCheckout = (): IMutationQuery<ICheckoutResponse> => {
  const { d2cUserId } = decodeAuthToken() ?? {};
  const meQuery = queryClient.getQueryData<IUserConfig>(
    authenticateMeQuery().queryKey,
  );
  const { firstName } = meQuery ?? {};
  const customer = {
    customerId: d2cUserId,
    source: "PERSONAL",
    name: firstName,
  };
  return {
    mutationKey: ['instant-checkout'],
    // @ts-ignore
    mutationFn: async (items: Array<ICheckoutItem>) => {
      const idempotentKey = await generateIdempotentKey(
        d2cUserId ?? '',
        items.map((item) => ({
          id: item.benefitId,
          quantity: item.quantity,
        })),
      );

      const payload = {
        customer,
        items,
      };

      window.dev.i('Payload for instant checkout', payload, idempotentKey);

      const response = await ordersAxiosInstance.post<ICheckoutResponse>(
        '/v2/orders/instant-checkout',
        payload,
        {
          headers: {
            ['x-idempotent-key']: idempotentKey,
          },
        },
      );
      return response.data;
    },
  };
};

export const getOrderDetails = (orderId: string): IQuery<IOrder> => {
  return {
    queryKey: ['order-details', orderId],
    queryFn: async () => {
      const response = await ordersAxiosInstance.get<IOrder>(
        `/v2/orders/${orderId}`,
      );
      return response.data;
    },
  };
}

export const getSkuRedemptionDetails = (benefitId: string): IQuery<ISKURedemption> => {
  const { d2cUserId, organisationId } = decodeAuthToken() ?? {}
  return {
    queryKey: ['sku-redemption-details', benefitId],
    queryFn: async () => {
      const payload = {
        memberId: d2cUserId,
        source: "PERSONAL",
        organisationId,
        benefitId,
      }
      const response = await employeeExperienceV3AxiosInstance.post<ISKURedemption>(
        `/health-benefit/redemption/status`,
        payload
      );
      return response.data;
    },
  };
}

export const getSkuCount = (skuId: string): IQuery<{
  count: number;
  remaining: number;
}> => {
  return {
    queryKey: ['sku-count', skuId],
    queryFn: async () => {
      const response = await ordersAxiosInstance.get<{
        count: number;
        remaining: number;
      }>(`/v2/orders/skus/${skuId}/count`)
      return response.data;
    }

  }
}

export const getRetailBenefitMembership = (benefitId: string): IQuery<{ isMembershipPresent: boolean; fulfillments: Array<IFulfillmentDetails> }> => {
  return {
    queryKey: ['retail-benefit-membership', benefitId],
    queryFn: async () => {
      const response = await employeeExperienceV3AxiosInstance.get<{
        isMembershipPresent: boolean;
        fulfillments: Array<IFulfillmentDetails>
      }>(`/benefit/${benefitId}/membership`)
      return response.data;
    }
  }
}